/**
 * useConfirm — единый CDP-модал подтверждения вместо нативного window.confirm().
 *
 * Нативный confirm() уродлив, не темизируется и рвёт стиль. Композабл повторяет
 * подход useToast (синглтон-состояние вне Pinia): любой вызывающий делает
 *   `if (!(await confirm({ message }))) return;`
 * а глобальный `<ConfirmHost />` (смонтирован в App.vue) рисует диалог и
 * резолвит промис по клику «Подтвердить»/«Отмена».
 *
 * Очередь на случай двух подтверждений подряд — рисуем queue[0].
 */
import { reactive } from "vue";

export type ConfirmTone = "danger" | "primary";

export interface ConfirmOptions {
  /** Заголовок; по умолчанию ConfirmHost подставит «Подтвердите действие». */
  title?: string;
  /** Основной текст вопроса (обязателен). */
  message: string;
  /** Подпись кнопки подтверждения; по умолчанию — common.confirm / для danger — common.delete. */
  confirmLabel?: string;
  /** Подпись кнопки отмены; по умолчанию — common.cancel. */
  cancelLabel?: string;
  /** Тон: danger (красная кнопка, по умолчанию — удаление/архив) или primary. */
  tone?: ConfirmTone;
}

export interface ConfirmRequest extends Required<Omit<ConfirmOptions, "title" | "confirmLabel" | "cancelLabel">> {
  id: number;
  title: string | null;
  confirmLabel: string | null;
  cancelLabel: string | null;
  resolve: (ok: boolean) => void;
}

export const confirmState = reactive<{ queue: ConfirmRequest[] }>({ queue: [] });
let seq = 0;

/**
 * Показать CDP-модал подтверждения. Возвращает Promise<boolean>:
 * true — пользователь подтвердил, false — отменил/закрыл.
 * Принимает либо строку-сообщение, либо объект опций.
 *
 * Standalone-функция (модульный синглтон) — можно импортировать напрямую,
 * без инстанса композабла: `import { confirmDialog } from ".../useConfirm"`.
 */
export function confirmDialog(opts: ConfirmOptions | string): Promise<boolean> {
  const o: ConfirmOptions = typeof opts === "string" ? { message: opts } : opts;
  return new Promise<boolean>((resolve) => {
    confirmState.queue.push({
      id: ++seq,
      title: o.title ?? null,
      message: o.message,
      confirmLabel: o.confirmLabel ?? null,
      cancelLabel: o.cancelLabel ?? null,
      tone: o.tone ?? "danger",
      resolve,
    });
  });
}

/** Резолвит текущий (верхний) запрос — вызывает ConfirmHost. */
export function resolveTopConfirm(ok: boolean): void {
  const req = confirmState.queue.shift();
  req?.resolve(ok);
}

/** Композабл-обёртка для единообразия с useToast. */
export function useConfirm() {
  return { state: confirmState, confirm: confirmDialog, resolveTop: resolveTopConfirm };
}
